/**
 * API 客户端 — 全局 Axios 实例
 * @ref: master.md § 2 System Architecture / Security
 */
import axios, { type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { unwrapApiPayload } from './payload';
import type { ApiClientError } from './apiClientError';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('uvp_access_token');
    if (token && config.headers) config.headers['Authorization'] = `Bearer ${token}`;
    // 确保所有请求都带有 X-Workspace-Id 头
    const workspaceId = localStorage.getItem('uvp_current_workspace_id') || '1';
    if (config.headers) config.headers['X-Workspace-Id'] = workspaceId;
    return config;
  },
  e => Promise.reject(e)
);

axiosInstance.interceptors.response.use(
  r => unwrapApiPayload(r.data),
  async error => {
    const req = error.config;
    if (error.response?.status === 401 && !req._retry) {
      req._retry = true;
      const refresh = localStorage.getItem('uvp_refresh_token');
      if (refresh) {
        try {
          const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken: refresh });
          const accessToken = res.data?.data?.accessToken ?? res.data?.accessToken;
          if (!accessToken) {
            throw new Error('refresh accessToken missing');
          }
          localStorage.setItem('uvp_access_token', accessToken);
          req.headers['Authorization'] = `Bearer ${accessToken}`;
          return axiosInstance(req);
        } catch {
          localStorage.removeItem('uvp_access_token');
          localStorage.removeItem('uvp_refresh_token');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    const msg =
      error.response?.data?.message ||
      error.response?.data?.msg ||
      error.message ||
      '网络异常，请稍后重试';
    const wrapped = new Error(msg) as ApiClientError;
    wrapped.status = error.response?.status;
    return Promise.reject(wrapped);
  }
);

const apiClient = {
  request<T = unknown, D = unknown>(config: AxiosRequestConfig<D>) {
    return axiosInstance.request<unknown, T, D>(config);
  },
  get<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return axiosInstance.get<unknown, T>(url, config);
  },
  delete<T = unknown>(url: string, config?: AxiosRequestConfig) {
    return axiosInstance.delete<unknown, T>(url, config);
  },
  post<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.post<unknown, T, D>(url, data, config);
  },
  put<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.put<unknown, T, D>(url, data, config);
  },
  patch<T = unknown, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig<D>) {
    return axiosInstance.patch<unknown, T, D>(url, data, config);
  },
  defaults: axiosInstance.defaults,
  interceptors: axiosInstance.interceptors,
};

export default apiClient;
